// ── ROBOFLOW FLOOR-PLAN DETECTION ────────────────────────────────────────────
// Sends an uploaded floor plan to a Roboflow object-detection model and returns
// the raw predictions. Nothing in here knows about the editor or React — the
// conversion to 2D vectors lives in floorPlanVectors.js, and the mapping onto
// the editor's own room/wall model in floorPlanToRooms.js.
//
// ══ WHAT THE MODEL ACTUALLY RETURNS ═════════════════════════════════════════
//
// The configured model (floor-plan-f2lla) has EIGHT classes:
//
//   entrance · bed · door · window · kitchen · living-room · bathroom · bedroom
//
// There is NO `wall` class, which is the single most important fact about this
// integration: walls cannot be detected, they are DERIVED from the rectangles of
// the detected rooms (see floorPlanVectors.analysePlan). The class maps
// below still carry a `wall` entry so that a model which does emit walls works
// through the same pipeline without a code change.
//
// Response shape (Roboflow hosted inference, object detection):
//   {
//     image:       { width, height },          // ORIGINAL pixels, not 640×640
//     predictions: [{ x, y, width, height, confidence, class, class_id }]
//   }
// `x`/`y` are the bounding box CENTRE, not its corner.

const API_HOST = 'https://detect.roboflow.com';

// Model is "<project>/<version>". The project has 7 versions; v1 is the one
// currently configured. Point REACT_APP_ROBOFLOW_MODEL at a newer one to upgrade
// without touching code.
const MODEL   = process.env.REACT_APP_ROBOFLOW_MODEL || 'floor-plan-f2lla/1';
const API_KEY = process.env.REACT_APP_ROBOFLOW_API_KEY || '';

// Detection tuning. Confidence is deliberately lowish: a missed room leaves a
// hole in the model, whereas a spurious one is easy to delete in the builder.
export const DEFAULT_CONFIDENCE = 30;   // percent
export const DEFAULT_OVERLAP    = 50;   // percent — NMS threshold

// Roboflow's own labels, grouped by what they mean to us. Anything not listed is
// ignored rather than guessed at.
export const ROOM_CLASSES = ['kitchen', 'living-room', 'bathroom', 'bedroom', 'entrance'];
export const OPENING_CLASSES = ['door', 'window'];

/** Roboflow room label → the app's own ROOM_TYPES id. */
export const CLASS_TO_ROOM_TYPE = {
  kitchen:       'kitchen',
  'living-room': 'living',
  bathroom:      'bathroom',
  bedroom:       'bedroom',
  entrance:      'hallway',
};

export function isRoomClass(cls)    { return ROOM_CLASSES.includes(cls); }
export function isOpeningClass(cls) { return OPENING_CLASSES.includes(cls); }
export function isWallClass(cls)    { return cls === 'wall'; }

export function isRoboflowConfigured() { return Boolean(API_KEY); }

// A missing key is almost always a dev server started before .env was written —
// CRA inlines REACT_APP_* at BOOT, so editing .env does nothing until restart,
// and the on-screen message cannot say that to an end user. Warn once, in the
// console, where the person who can fix it will look.
if (!API_KEY && typeof console !== 'undefined') {
  console.warn(
    '[roboflow] REACT_APP_ROBOFLOW_API_KEY is empty — floor plan detection is off.\n'
    + 'If the key IS in .env, restart the dev server: CRA reads .env only at startup.',
  );
}

/**
 * Reads a File into the bare base64 payload Roboflow expects.
 *
 * FileReader hands back a data URL ("data:image/png;base64,AAAA…"); the API
 * wants only the part after the comma. Sending the whole data URL is the classic
 * cause of "Could not load input image".
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('file-read-failed'));
    reader.readAsDataURL(file);
  });
}

/** Natural pixel size of an image File, needed to map detections into metres. */
export function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image-decode-failed')); };
    img.src = url;
  });
}

/**
 * Runs detection on an uploaded floor plan.
 *
 * @param {File}   file                the user's image
 * @param {object} [opts]
 * @param {number} [opts.confidence]   percent, 0–100
 * @param {number} [opts.overlap]      percent, 0–100
 * @param {AbortSignal} [opts.signal]  so a closed dialog cancels the request
 * @returns {Promise<{image:{width,height}, predictions:Array}>}
 */
export async function detectFloorPlan(file, opts = {}) {
  if (!API_KEY) throw new Error('roboflow-not-configured');
  if (!file) throw new Error('no-file');

  const {
    confidence = DEFAULT_CONFIDENCE,
    overlap    = DEFAULT_OVERLAP,
    signal,
  } = opts;

  const base64 = await fileToBase64(file);
  const url = `${API_HOST}/${MODEL}`
    + `?api_key=${encodeURIComponent(API_KEY)}`
    + `&confidence=${confidence}&overlap=${overlap}`;

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      // Roboflow's base64 endpoint reads the RAW body; it is not form-encoded
      // key=value despite the content type it asks for.
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: base64,
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new Error('roboflow-network');
  }

  const text = await res.text();
  let json;
  try { json = JSON.parse(text); }
  catch { throw new Error('roboflow-bad-response'); }

  if (!res.ok || json?.message) {
    console.error('[roboflow] detection failed:', res.status, json?.message || text.slice(0, 200));
    throw new Error('roboflow-failed');
  }

  // The reported image size is what every downstream pixel→metre conversion is
  // relative to. Fall back to reading the file if the API omits it.
  let image = json.image;
  if (!image?.width || !image?.height) {
    image = await readImageSize(file).catch(() => ({ width: 0, height: 0 }));
  }

  return {
    image,
    predictions: Array.isArray(json.predictions) ? json.predictions : [],
  };
}
